import {
  bySubstance,
  countLabel,
  disclosure,
  disclosureCss,
  gapRows,
  group,
  groupsCss,
  kitPartCss,
  microHeading,
  microHeadingCss,
  panel,
  panelAction,
  panelActionCss,
  panelCss,
  positive,
  rowList,
  rowsCss,
  warningChip,
  warningChipCss,
  warningChipIn,
} from '../armKit';
import { bookmarkCounts, mentionCounts } from '../batchFetchers';
import {
  analyticsDocumentUrl,
  profileEditUrl,
  upgradeUrl,
  uploadDocumentUrl,
  workEditUrl,
} from '../destinations';
import {
  profileGaps,
  viewedPageName,
  viewedUser,
  viewerIsPremium,
} from '../profileFacts';
import {
  cardPartCss,
  chipsRowCss,
  compactCardsCss,
  cssRule,
  EDGE,
  HAIRLINE,
  INK,
  mediaCss,
  MUTED,
  stackWorksFirstCss,
  worksFirstCss,
} from '../surface';

import type { Row } from '../armKit';
import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';
import type { ProfileCard } from '../surface';

type Facts = Map<number, WorkFacts>;
type Gap = { workId: number; title: string; hasDownloadable: boolean };

const FLAG = 'ownerdose';

const BOOKMARKS_SOURCE = 'profile-liautotest-ownerdose-bookmarks';
const DOWNLOAD_SOURCE = 'profile-liautotest-ownerdose-download';
const EDIT_SOURCE = 'profile-liautotest-ownerdose-edit';
const EXPAND_SOURCE = 'profile-liautotest-ownerdose-expand';
const FIX_SOURCE = 'profile-liautotest-ownerdose-fix';
const IMPACT_SOURCE = 'profile-liautotest-ownerdose-impact';
const MENTIONS_SOURCE = 'profile-liautotest-ownerdose-mentions';
const NO_FILE_SOURCE = 'profile-liautotest-ownerdose-no-file';
const PROFILE_SOURCE = 'profile-liautotest-ownerdose-profile';
const UPLOAD_SOURCE = 'profile-liautotest-ownerdose-upload';

const EDIT = 'Edit';
const DOWNLOAD = 'Download';
const IMPACT = 'View impact';
const NO_FILE = 'No file';
const PROFILE_HEADING = 'Missing from your profile';
const UPLOAD_LABEL = 'Add a paper';
const UPLOAD_ENTRY = 'profile';
// workFacts stops at the first 100 works, so a longer corpus is only sampled.
const FACTS_LIMIT = 100;
const LEAD_GAPS = 3;

const GAP_TITLE_CLASS = 'profile-liautotest-ownerdose__gap-title';
const GAP_NOTE_CLASS = 'profile-liautotest-ownerdose__gap-note';
const UPLOAD_CLASS = 'profile-liautotest-ownerdose__upload';

// One console, sectioned by a hairline rather than by five bordered cards: the
// count heads it, the reach that rewards reading comes next, then the two asks.
function consoleCss(scope: string): string {
  return (
    panelCss(scope, { marginBottom: '0' }) +
    kitPartCss(
      'panelTitle',
      scope,
      'font-size: 20px; line-height: 1.3; margin-bottom: 12px;',
    ) +
    groupsCss(scope, { rule: `1px solid ${HAIRLINE}` }) +
    microHeadingCss(scope) +
    disclosureCss(scope) +
    kitPartCss('summary', scope, 'margin-top: 10px; font-weight: 600;') +
    rowsCss(scope, { marginTop: '8px', itemPadding: '4px 0' }) +
    panelActionCss(scope, { fontSize: '12px', marginTop: '0' }) +
    cssRule(
      [`${scope} .${GAP_TITLE_CLASS}`],
      'font-size: 16px; font-weight: 700; line-height: 1.35; ' +
        `color: ${INK}; margin: 0;`,
    ) +
    cssRule(
      [`${scope} .${GAP_NOTE_CLASS}`],
      `font-size: 13px; line-height: 1.4; color: ${MUTED}; margin: 4px 0 0;`,
    ) +
    cssRule(
      [`${scope} .${UPLOAD_CLASS}`],
      'display: block; margin-top: 14px; padding-top: 14px; ' +
        `border-top: 1px solid ${EDGE}; font-size: 14px;`,
    )
  );
}

function listCss(scope: string): string {
  return (
    worksFirstCss(scope) +
    compactCardsCss(scope) +
    cardPartCss('title', scope, 'line-height: 1.45;') +
    cardPartCss('body', scope, 'align-content: flex-start;') +
    chipsRowCss(scope, {
      nowrap: true,
      gap: '0 12px',
      lineHeight: '1.45',
      tabularNums: true,
      pill: false,
    }) +
    cardPartCss('chips', scope, 'flex: 1 0 100%;') +
    mediaCss(
      'phone',
      cardPartCss('chips', scope, 'flex-wrap: wrap; white-space: normal;'),
    ) +
    cardPartCss('thumbnail', scope, 'display: block !important; zoom: 0.36;') +
    warningChipCss(scope) +
    stackWorksFirstCss(scope)
  );
}

function marked(card: ProfileCard): boolean {
  return card.flag(FLAG) != null;
}

function linkedChip(
  card: ProfileCard,
  ctx: ArmContext,
  label: string,
  href: string,
  source: string,
): void {
  const chip = panelAction(label, href, () =>
    ctx.track(source, { entityId: card.workId }),
  );
  card.chipsRow()?.appendChild(chip);
}

function impactUrl(workId: number): string | null {
  if (!viewerIsPremium()) return upgradeUrl('analytics', IMPACT_SOURCE);

  const user = viewedUser();
  const pageName = user?.page_name;
  if (pageName == null || pageName === '') return null;

  return analyticsDocumentUrl(pageName, workId, user?.domain_name);
}

function statusChip(card: ProfileCard, ctx: ArmContext): void {
  if (card.hasDownloadable) return;

  const chip = warningChip(NO_FILE, workEditUrl(card.workId), () =>
    ctx.track(NO_FILE_SOURCE, { entityId: card.workId }),
  );
  card.chipsRow()?.appendChild(chip);
}

function compact(card: ProfileCard, ctx: ArmContext): void {
  card.setFlag(FLAG);
  card.collapse();
  linkedChip(card, ctx, EDIT, workEditUrl(card.workId), EDIT_SOURCE);
  if (card.downloadUrl != null) {
    linkedChip(card, ctx, DOWNLOAD, card.downloadUrl, DOWNLOAD_SOURCE);
  }
  const impact = impactUrl(card.workId);
  if (impact != null) linkedChip(card, ctx, IMPACT, impact, IMPACT_SOURCE);
  statusChip(card, ctx);
}

function countChip(
  card: ProfileCard,
  ctx: ArmContext,
  count: number | null,
  noun: string,
  feature: string,
  source: string,
): void {
  const label = countLabel(count, noun);
  if (label == null) return;

  const href = viewerIsPremium()
    ? card.url
    : upgradeUrl(feature, source, card.workId);
  if (href == null) {
    card.addChip(label);
    return;
  }

  linkedChip(card, ctx, label, href, source);
}

function tally(
  card: ProfileCard,
  ctx: ArmContext,
  views?: number | null,
  readers?: number,
  mentions?: number,
): void {
  const viewsChip = countLabel(positive(views), 'view');
  if (viewsChip != null) card.addChip(viewsChip);

  countChip(
    card,
    ctx,
    positive(readers),
    'bookmark',
    'readers',
    BOOKMARKS_SOURCE,
  );
  countChip(
    card,
    ctx,
    positive(mentions),
    'mention',
    'mentions',
    MENTIONS_SOURCE,
  );

  // The counts land after the status chip did, and addChip can only append.
  const row = card.chipsRow();
  const status = row == null ? null : warningChipIn(row);
  if (row != null && status != null) row.appendChild(status);
}

function gapsIn(facts: Facts, ctx: ArmContext): Gap[] {
  const rendered = new Map(ctx.cards().map((card) => [card.workId, card]));

  return Array.from(facts.entries())
    .flatMap(([workId, fact]) => {
      // Only an explicit false is a work with no file; an unknown is a work the
      // endpoint did not describe, and listing it would be an accusation.
      if (fact.hasPdf !== false) return [];
      // The rendered card outranks the fact: it is what a reader actually
      // sees, and a "no file" row beside a real download link would
      // contradict the page rather than remind the author of anything.
      if (rendered.get(workId)?.hasDownloadable === true) return [];

      const title = (fact.title ?? rendered.get(workId)?.title ?? '').trim();

      return title === '' ? [] : [{ workId, title, hasDownloadable: false }];
    })
    .sort(bySubstance((workId) => facts.get(workId)?.pageCount));
}

function gapTitleFor(gapCount: number, sampled: boolean): string {
  const subject = sampled
    ? `${gapCount} of your first ${FACTS_LIMIT} papers`
    : `${gapCount} ${gapCount === 1 ? 'paper' : 'papers'}`;

  return `${subject} ${gapCount === 1 ? 'has' : 'have'} no file to download`;
}

function gapNoteFor(gapCount: number): string {
  const object = gapCount === 1 ? 'it' : 'these';

  return `Readers can't download ${object} until you add a file.`;
}

function gapLead(gapCount: number, sampled: boolean): DocumentFragment {
  const lead = document.createDocumentFragment();

  const title = document.createElement('p');
  title.className = GAP_TITLE_CLASS;
  title.textContent = gapTitleFor(gapCount, sampled);
  lead.appendChild(title);

  const note = document.createElement('p');
  note.className = GAP_NOTE_CLASS;
  note.textContent = gapNoteFor(gapCount);
  lead.appendChild(note);

  return lead;
}

function filesGroup(
  gaps: Gap[],
  sampled: boolean,
  ctx: ArmContext,
): HTMLElement {
  const element = group('files');

  if (gaps.length > 0) {
    element.appendChild(gapLead(gaps.length, sampled));

    const rows: Row[] = gaps.map((gap) => ({
      title: gap.title,
      url: workEditUrl(gap.workId),
      onClick: () => ctx.track(FIX_SOURCE, { entityId: gap.workId }),
    }));
    element.appendChild(rowList(rows.slice(0, LEAD_GAPS)));

    const rest = rows.slice(LEAD_GAPS);
    if (rest.length > 0) {
      element.appendChild(
        disclosure(`View ${rest.length} more`, rowList(rest), () => {
          ctx.track(EXPAND_SOURCE);
        }),
      );
    }
  }

  const action = panelAction(
    UPLOAD_LABEL,
    uploadDocumentUrl(UPLOAD_ENTRY),
    () => {
      ctx.track(UPLOAD_SOURCE);
    },
  );
  action.classList.add(UPLOAD_CLASS);
  element.appendChild(action);

  return element;
}

function profileGroup(fields: Row[]): HTMLElement {
  const element = group('profile');
  element.appendChild(microHeading(PROFILE_HEADING));
  element.appendChild(rowList(fields));

  return element;
}

export const ownerFullDose: ArmSpec = {
  onReady: async (ctx) => {
    const cards = ctx.cards();
    const styles =
      consoleCss(ctx.scope) + (cards.length > 0 ? listCss(ctx.scope) : '');

    // The list treatment is synchronous and above the fold; the console waits
    // on four signals, so it mounts nothing until it knows it has something
    // to say.
    if (cards.length > 0) {
      ctx.injectStyles(styles);
      cards.forEach((card) => compact(card, ctx));
    }

    const workIds = cards.map((card) => card.workId);
    const [facts, readers, mentions] = await Promise.all([
      ctx.workFacts(),
      bookmarkCounts(workIds),
      mentionCounts(workIds),
    ]);

    cards.forEach((card) => {
      tally(
        card,
        ctx,
        facts.get(card.workId)?.views,
        readers.get(card.workId),
        mentions.get(card.workId),
      );
    });

    const gaps = gapsIn(facts, ctx);
    const pageName = viewedPageName();
    const fields =
      pageName == null
        ? []
        : gapRows(profileGaps(), profileEditUrl(pageName), (field) => {
            ctx.track(PROFILE_SOURCE, { extra: { field } });
          });

    if (gaps.length === 0 && fields.length === 0) return;

    const slot = ctx.mountSlot('columnTop');
    if (slot == null) return;

    ctx.injectStyles(styles);

    const { root, body } = panel();
    body.appendChild(filesGroup(gaps, facts.size >= FACTS_LIMIT, ctx));
    if (fields.length > 0) body.appendChild(profileGroup(fields));

    slot.appendChild(root);
  },

  // Collapsed and statused, never counted: a scroll batch is not worth a
  // second round trip. Gated on the marks onReady left, so a batch cannot
  // engage an arm that degraded to control.
  onCards: (cards, ctx) => {
    if (!ctx.cards().some(marked)) return;

    cards.filter((card) => !marked(card)).forEach((card) => compact(card, ctx));
  },
};
